import React, { useState, useEffect } from 'react';
import { getPokemonSpriteUrl, getPokemonIdFromName } from '../../api/pokeApi';

const PokemonSprite = ({ pokemonName, pokemonNumber, style, alt, size = "medium", ...props }) => {
  const [spriteUrl, setSpriteUrl] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const loadSprite = async () => {
      setIsLoading(true);
      setError(false);

      // If a full image URL was passed (e.g. a Limitless icon URL from the
      // synced taxonomy), use it directly.
      const directUrl = [pokemonNumber, pokemonName].find(
        (v) => typeof v === 'string' && /^https?:\/\//i.test(v)
      );
      if (directUrl) {
        setSpriteUrl(directUrl);
        setIsLoading(false);
        return;
      }

      // If we have a pokemonNumber, use it directly. Form/Mega entries
      // (PokeAPI id >= 10000) usually only have official artwork, so use that.
      if (pokemonNumber) {
        setSpriteUrl(Number(pokemonNumber) >= 10000
          ? `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/official-artwork/${pokemonNumber}.png`
          : `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${pokemonNumber}.png`);
        setIsLoading(false);
        return;
      }
      
      if (!pokemonName) {
        setIsLoading(false);
        setError(true);
        return;
      }
      
      try {
        // Use the cached sprite loading system
        const url = await getPokemonSpriteUrl(pokemonName);
        setSpriteUrl(url);
        setIsLoading(false);
      } catch (err) {
        console.error(`Error loading sprite for ${pokemonName}:`, err);
        setError(true);
        setIsLoading(false);
        
        // Fallback to PokeAPI direct URL as final option
        try {
          const id = getPokemonIdFromName(pokemonName);
          setSpriteUrl(`https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${id}.png`);
        } catch {
          setSpriteUrl('https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/25.png');
        }
      }
    };
    
    loadSprite();
  }, [pokemonName, pokemonNumber]);
  
  // Get size based on prop
  const getSpriteSize = () => {
    switch(size) {
      case 'small': return 40;
      case 'large': return 64;
      default: return 48;
    }
  };
  
  const spriteSize = getSpriteSize();

  // Common container style to prevent layout shift
  const containerStyle = {
    width: `${spriteSize}px`,
    height: `${spriteSize}px`,
    display: 'inline-block',
    position: 'relative',
    ...style
  };

  if (isLoading) {
    return (
      <div 
        className="sprite-placeholder" 
        style={{
          ...containerStyle,
          backgroundColor: 'var(--border-color)',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '12px',
          color: 'var(--text-secondary)'
        }}
      >
        ...
      </div>
    );
  }
  
  return (
    <div style={containerStyle} className="pokemon-sprite-container">
      <img 
        src={spriteUrl}
        alt={alt || pokemonName || `Pokemon #${pokemonNumber}`}
        width={spriteSize}
        height={spriteSize}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain'
        }}
        loading="lazy"
        decoding="async"
        onError={(e) => {
          const current = e.target.src;
          const officialArtwork = 'other/official-artwork';
          const id = pokemonNumber || (pokemonName ? getPokemonIdFromName(pokemonName) : null);
          // First failure: try official artwork (covers Megas/forms with no default sprite).
          if (id && !current.includes(officialArtwork)) {
            setError(true);
            e.target.src = `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/other/official-artwork/${id}.png`;
          } else {
            // Final fallback.
            e.target.onerror = null;
            e.target.src = "https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/25.png";
          }
        }}
        {...props}
      />
    </div>
  );
};

export default PokemonSprite;